<script>
import { defineComponent as _defineComponent } from "vue";
import { createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = ["width", "height"];
import { shallowRef, onMounted, watch, computed, inject } from "vue";
import { useDevicePixelRatio } from "@vueuse/core";
import { usePlayerControlsApi } from "../composables/private/usePlayerControlsApi";
import { MagicPlayerInstanceId, MagicPlayerOptionsKey } from "../symbols";
export default /* @__PURE__ */ _defineComponent({
  __name: "MagicPlayerMuxPopover",
  props: {
    playbackId: { type: String, required: false }
  },
  setup(__props) {
    const instanceId = inject(MagicPlayerInstanceId, void 0);
    const injectedOptions = inject(MagicPlayerOptionsKey, void 0);
    if (!instanceId || !injectedOptions) {
      throw new Error(
        "MagicPlayerMuxPopover must be nested inside MagicPlayerVideoControls."
      );
    }
    const { seekedTime } = usePlayerControlsApi({ id: instanceId });
    const { pixelRatio } = useDevicePixelRatio();
    const canvasRef = shallowRef();
    const storyboard = shallowRef();
    let context = void 0;
    let image = void 0;
    const thumbWidth = computed(() => {
      if (!storyboard.value) return 0;
      return storyboard.value.tile_width / pixelRatio.value;
    });
    const thumbHeight = computed(() => {
      if (!storyboard.value) return 0;
      return storyboard.value.tile_height / pixelRatio.value;
    });
    function getMuxId(url) {
      const match = url?.match(/mux\.com\/([^\/]+)/);
      return match?.[1];
    }
    async function init() {
      const parsedPlaybackId = getMuxId(injectedOptions?.src);
      const mappedPlaybackId = __props.playbackId ?? parsedPlaybackId;
      if (!mappedPlaybackId) return;
      try {
        storyboard.value = await fetch(
          `https://image.mux.com/${mappedPlaybackId}/storyboard.json`
        ).then((res) => res.json());
        if (!storyboard.value) throw new Error();
        image = new Image();
        image.src = storyboard.value.url;
        await image.decode();
        context = canvasRef.value.getContext("2d");
        context.drawImage(image, 0, 0);
      } catch (e) {
        console.error("Can not initialize timeine preview.", e);
      }
    }
    function drawFrame(time) {
      if (!storyboard.value || !context || !image) return;
      const { tile_height, tile_width, tiles } = storyboard.value;
      let closestIndex = -1;
      let minDifference = Infinity;
      for (let i = 0; i < tiles.length; i++) {
        const { start } = tiles[i];
        const difference = Math.abs(start - time);
        if (difference < minDifference) {
          minDifference = difference;
          closestIndex = i;
        }
      }
      const tile = tiles[closestIndex];
      context.drawImage(
        image,
        tile.x,
        tile.y,
        tile_width,
        tile_height,
        0,
        0,
        tile_width,
        tile_height
      );
    }
    onMounted(init);
    watch(() => seekedTime?.value, drawFrame);
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock(
        "div",
        {
          style: _normalizeStyle({
            width: `${thumbWidth.value}px`,
            height: `${thumbHeight.value}px`
          }),
          class: "magic-player-mux-popover"
        },
        [
          _createElementVNode("canvas", {
            ref_key: "canvasRef",
            ref: canvasRef,
            width: storyboard.value?.tile_width,
            height: storyboard.value?.tile_height
          }, null, 8, _hoisted_1)
        ],
        4
        /* STYLE */
      );
    };
  }
});
</script>

<style>
.magic-player-mux-popover {
  border-radius: var(--magic-player-popover-border-radius, 0.25rem);
  overflow: hidden;
}

.magic-player-mux-popover canvas {
  width: 100%;
  height: auto;
}
</style>
